import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PlusIcon } from "../../components/icons";
import { shortTime } from "../../lib/format";
import { useCases, useCreateQuote, usePartRequests, useQuotes } from "../../lib/queries";
import { money, quoteNumber, STATUS_LABEL, STATUS_PILL, shownStatus } from "../quotes/quote-text";
import { CaseDialog } from "./CaseDialog";
import { caseNumber } from "./case-text";
import { PartRequestDialog, PartRequestLinks } from "./PartRequestDialog";

/**
 * A customer's after-sales cases, part requests and quotes, and the buttons to open one,
 * beside the chat and on the contact's page. From a chat, they remember the conversation.
 */
export function ContactCases({
  contact,
  conversationId,
}: {
  contact: { id: string; name: string | null; waId: string };
  conversationId?: string;
}) {
  const cases = useCases({ contactId: contact.id, state: "all" }).data ?? [];
  const requests = usePartRequests({ contactId: contact.id, state: "all" }).data ?? [];
  const quotes = useQuotes({ contactId: contact.id, state: "all" }).data ?? [];
  const [creating, setCreating] = useState<"case" | "part" | null>(null);
  const createQuote = useCreateQuote();
  const navigate = useNavigate();

  async function quote() {
    const q = await createQuote.mutateAsync({ contactId: contact.id, conversationId: conversationId ?? null });
    navigate({ to: "/cotizaciones/$quoteId", params: { quoteId: q.id } });
  }

  return (
    <div className="contact-cases">
      {cases.length > 0 && (
        <ul className="contact-cases-list">
          {cases.map((c) => (
            <li key={c.id}>
              <Link to="/postventa/$caseId" params={{ caseId: c.id }} className="contact-case">
                <span className="mono">{caseNumber(c.number)}</span>
                <span className="contact-case-title">{c.title}</span>
                <span className={`pill ${c.closed ? "pill-closed" : "pill-human"}`}>{c.statusLabel}</span>
                <span className="faint">{shortTime(c.updatedAt)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {requests.length > 0 && <PartRequestLinks items={requests} />}
      {quotes.length > 0 && (
        <ul className="contact-cases-list">
          {quotes.map((x) => {
            const status = shownStatus(x);
            return (
              <li key={x.id}>
                <Link to="/cotizaciones/$quoteId" params={{ quoteId: x.id }} className="contact-case">
                  <span className="mono">{quoteNumber(x.number)}</span>
                  <span className="contact-case-title">{money(x.total, x.currency)}</span>
                  <span className={`pill ${STATUS_PILL[status]}`}>{STATUS_LABEL[status]}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      <div className="contact-cases-actions">
        <button className="btn btn-sm" type="button" onClick={() => setCreating("case")}>
          <PlusIcon />
          Crear caso
        </button>
        <button className="btn btn-sm" type="button" onClick={() => setCreating("part")}>
          <PlusIcon />
          Pedir repuesto
        </button>
        <button className="btn btn-sm" type="button" disabled={createQuote.isPending} onClick={quote}>
          <PlusIcon />
          Cotizar
        </button>
      </div>
      {creating === "case" && (
        <CaseDialog contact={contact} conversationId={conversationId} onClose={() => setCreating(null)} />
      )}
      {creating === "part" && (
        <PartRequestDialog contact={contact} conversationId={conversationId} onClose={() => setCreating(null)} />
      )}
    </div>
  );
}
